import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2, RefreshCw, Sparkles, Check, X, TrendingUp, TrendingDown, Minus, ShieldCheck, Clock, AlertCircle, Globe, ChevronDown, ChevronRight, BookOpen, ArrowRight, GitCompare,
} from 'lucide-react';
import {
  adminKbCandidates, adminKbReviewCandidate, adminKbLearnDomain, adminKbLearnStatus, adminKbApproveCandidate, adminKbRejectCandidate, adminKbSetLearningWindow,
  type KbCandidateRow, type KbLearnVerdict, type KbCandidateNote,
} from '../../services/adminService';
import SourceRegistryEditor from './SourceRegistryEditor';
import SelfTestSection from './SelfTestSection';
import { useConfirm } from '../../hooks/useConfirm';

const numInput = 'w-16 rounded-md border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-2 py-1 text-sm tabular-nums focus:outline-none focus:border-secondary-500';

const VERDICT: Record<KbLearnVerdict, { label: string; cls: string; Icon: typeof TrendingUp }> = {
  improved: { label: 'Improved', cls: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40', Icon: TrendingUp },
  regressed: { label: 'Regressed', cls: 'text-rose-600 bg-rose-50 dark:bg-rose-950/40', Icon: TrendingDown },
  equal: { label: 'No change', cls: 'text-secondary-500 bg-secondary-100 dark:bg-secondary-800', Icon: Minus },
  unassessed: { label: 'Not benchmarked', cls: 'text-secondary-400 bg-secondary-100 dark:bg-secondary-800', Icon: Minus },
};

/** One signed delta (depth / breadth) with a direction arrow — grayscale, colored only by sign. */
const Delta: React.FC<{ label: string; value: number }> = ({ label, value }) => {
  const up = value > 0.0001; const down = value < -0.0001;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const cls = up ? 'text-emerald-600' : down ? 'text-rose-600' : 'text-secondary-400';
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <span className="text-secondary-500">{label}</span>
      <Icon className={`w-3.5 h-3.5 ${cls}`} />
      <span className={`tabular-nums ${cls}`}>{value > 0 ? '+' : ''}{value.toFixed(2)}</span>
    </span>
  );
};

/** One benchmark axis as a LIVE → PENDING comparison ("depth  0.42 → 0.58  +0.16"), colored by direction. */
const AxisCompare: React.FC<{ label: string; live: number; pending: number }> = ({ label, live, pending }) => {
  const delta = pending - live;
  const up = delta > 0.0001; const down = delta < -0.0001;
  const cls = up ? 'text-emerald-600' : down ? 'text-rose-600' : 'text-secondary-400';
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="capitalize text-secondary-500">{label}</span>
      <span className="inline-flex items-center gap-1.5 tabular-nums">
        <span className="text-secondary-400">{live.toFixed(2)}</span>
        <ArrowRight className="w-3 h-3 text-secondary-300" />
        <span className="font-medium text-secondary-900 dark:text-white">{pending.toFixed(2)}</span>
        <span className={`w-12 text-right ${cls}`}>{delta > 0 ? '+' : ''}{delta.toFixed(2)}</span>
      </span>
    </div>
  );
};

/** The live-vs-pending benchmark comparison for a candidate — shown only once a golden-probe benchmark has scored it. */
const BenchmarkCompare: React.FC<{ row: KbCandidateRow }> = ({ row }) => {
  const has = (row.depthLive ?? 0) + (row.depthPending ?? 0) + (row.breadthLive ?? 0) + (row.breadthPending ?? 0) > 0;
  if (!has) return null;
  return (
    <div className="mt-3 rounded-lg border border-secondary-200 dark:border-secondary-800 bg-secondary-50 dark:bg-secondary-900/40 p-2.5">
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-secondary-400">
        <GitCompare className="w-3 h-3" /> Benchmark · live → pending
      </div>
      <div className="space-y-1">
        <AxisCompare label="depth" live={row.depthLive ?? 0} pending={row.depthPending ?? 0} />
        <AxisCompare label="breadth" live={row.breadthLive ?? 0} pending={row.breadthPending ?? 0} />
      </div>
    </div>
  );
};

/** The live KB's freshness for a domain, framed as CALM auto-learning status (not an overdue alarm). The weekly
 *  scheduler refreshes every domain on its cadence, so "hasn't run yet" or "cadence elapsed" is NORMAL, not broken —
 *  we only flag `stale` (amber) when a domain is genuinely long-overdue (> 2× its cadence), which may need a look. */
function freshness(config: KbCandidateRow['config']): { text: string; overdue: boolean } {
  const cadence = Math.max(1, config.cadenceDays || 7);
  const parseMs = (s?: string | null): number | null => { const t = s ? Date.parse(s) : NaN; return Number.isFinite(t) ? t : null; };
  const refreshedMs = parseMs(config.lastPublishedAt) ?? parseMs(config.lastLearnedAt);
  const learnedMs = parseMs(config.lastLearnedAt) ?? parseMs(config.lastPublishedAt);
  const cadenceLabel = `auto-learning every ${cadence}d`;
  // Never run yet — the weekly job simply hasn't reached it. Calm, not an alarm.
  if (refreshedMs === null && learnedMs === null) return { text: `${cadenceLabel} · not run yet`, overdue: false };
  const days = (ms: number) => Math.max(0, Math.floor((Date.now() - ms) / 86400_000));
  const dueIn = learnedMs !== null ? cadence - days(learnedMs) : 0;
  // "next run in Nd" while inside the cadence; once elapsed it's just "refresh due" (normal — the job runs weekly).
  const nextText = dueIn > 0 ? `next run in ${dueIn}d` : 'refresh due';
  // STALE only when > 2× cadence behind — that's the genuinely worth-a-look case (amber).
  const stale = learnedMs !== null && days(learnedMs) > cadence * 2;
  if (refreshedMs === null) return { text: `${cadenceLabel} · ${nextText}`, overdue: stale };
  const age = days(refreshedMs);
  const rel = age <= 0 ? 'today' : age === 1 ? 'yesterday' : `${age}d ago`;
  const word = config.lastPublishedAt ? 'refreshed' : 'researched';
  return { text: `${word} ${rel} · ${nextText}`, overdue: stale };
}

/** One domain's learning card: evidence, sample notes, window controls, and the review actions. */
const CandidateCard: React.FC<{
  row: KbCandidateRow; busy: boolean; onAct: (fn: () => Promise<unknown>) => void; onLearn: () => void;
}> = ({ row, busy, onAct, onLearn }) => {
  const [months, setMonths] = useState(row.config.windowMonths ?? 24);
  const [cadence, setCadence] = useState(row.config.cadenceDays ?? 7);
  const [auto, setAuto] = useState(row.config.autoApprove);
  const [showSources, setShowSources] = useState(false);
  const [notes, setNotes] = useState<KbCandidateNote[] | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const pending = row.status === 'pending_review';
  const v = VERDICT[row.verdict];

  // Full-detail review of exactly what will be published — fetched on demand so the admin reads it BEFORE approving.
  const toggleReview = async () => {
    const next = !reviewOpen;
    setReviewOpen(next);
    if (next && notes === null) {
      setReviewing(true);
      try { setNotes((await adminKbReviewCandidate(row.domain)).notes); }
      catch { setNotes([]); }
      finally { setReviewing(false); }
    }
  };

  return (
    <div className="rounded-xl border border-secondary-200 dark:border-secondary-800 bg-white dark:bg-secondary-900 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-secondary-900 dark:text-white capitalize truncate">{row.domain}</h3>
            <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${pending ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'bg-secondary-100 text-secondary-500 dark:bg-secondary-800'}`}>
              {pending ? 'Pending review' : 'Idle'}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-secondary-500">
            <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${v.cls}`}><v.Icon className="w-3 h-3" />{v.label}</span>
            <Delta label="depth" value={row.depthDelta} />
            <Delta label="breadth" value={row.breadthDelta} />
            {row.updated_at && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(row.updated_at).toLocaleDateString()}</span>}
          </div>
          {(() => {
            // Live-KB freshness — makes the "stays current" moat visible + actionable (overdue domains flagged).
            const f = freshness(row.config);
            return (
              <div className={`mt-1 inline-flex items-center gap-1 text-xs ${f.overdue ? 'text-amber-600 dark:text-amber-400' : 'text-secondary-400'}`} title="How fresh this domain's live knowledge is, and when it is next due to refresh">
                {f.overdue ? <AlertCircle className="w-3 h-3" /> : <RefreshCw className="w-3 h-3" />} Live KB {f.text}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-secondary-600 dark:text-secondary-300">
        <span><b className="tabular-nums text-secondary-900 dark:text-white">+{row.ingested}</b> learned</span>
        <span><b className="tabular-nums">{row.redundant}</b> redundant</span>
        <span className={row.rejected ? 'text-rose-600' : ''}><b className="tabular-nums">{row.rejected}</b> blocked</span>
        <span><b className="tabular-nums">{row.cycles}</b> cycle{row.cycles === 1 ? '' : 's'}</span>
      </div>

      {/* Live vs pending benchmark — the eval that proves the candidate is actually better before you publish it. */}
      {pending && <BenchmarkCompare row={row} />}

      {row.samples.length > 0 && (
        <ul className="mt-3 space-y-1">
          {row.samples.slice(0, 3).map((s, i) => (
            <li key={i} className="truncate rounded-md bg-secondary-50 dark:bg-secondary-800/60 px-2 py-1 text-xs text-secondary-600 dark:text-secondary-300" title={s.preview}>
              <span className="text-secondary-400">{s.source}:</span> {s.preview}
            </li>
          ))}
        </ul>
      )}

      {/* Review-before-publish: read the FULL new notes (not just the 3 samples) this candidate would add to the KB. */}
      {pending && (
        <div className="mt-3">
          <button onClick={toggleReview} className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary-600 dark:text-secondary-300 hover:text-secondary-900 dark:hover:text-white">
            {reviewOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            <BookOpen className="w-3.5 h-3.5" /> Review what will be published{notes ? ` (${notes.length})` : ''}
          </button>
          {reviewOpen && (
            <div className="mt-2 max-h-80 overflow-y-auto rounded-lg border border-secondary-200 dark:border-secondary-800 bg-secondary-50 dark:bg-secondary-900/40 p-2 space-y-2">
              {reviewing ? (
                <div className="flex items-center gap-2 px-1 py-2 text-xs text-secondary-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading the full learned notes…</div>
              ) : !notes || notes.length === 0 ? (
                <p className="px-1 py-2 text-xs text-secondary-400">No new note detail to show.</p>
              ) : (
                notes.map((n, i) => (
                  <div key={i} className="rounded-md bg-white dark:bg-secondary-800/60 p-2">
                    <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-secondary-400">{n.source}</div>
                    <p className="whitespace-pre-wrap text-xs leading-relaxed text-secondary-700 dark:text-secondary-200">{n.text}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-secondary-100 dark:border-secondary-800 pt-3">
        <label className="flex items-center gap-1.5 text-xs text-secondary-500">window
          <input type="number" min={1} max={120} value={months} onChange={(e) => setMonths(Number(e.target.value))} className={numInput} disabled={busy} />mo
        </label>
        <label className="flex items-center gap-1.5 text-xs text-secondary-500">every
          <input type="number" min={1} max={90} value={cadence} onChange={(e) => setCadence(Number(e.target.value))} className={numInput} disabled={busy} />days
        </label>
        <label className="flex items-center gap-1.5 text-xs text-secondary-600 dark:text-secondary-300 cursor-pointer" title="Auto-publish a clear improvement with zero blocked notes (low-risk domains only)">
          <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} disabled={busy} className="rounded" />
          <ShieldCheck className="w-3.5 h-3.5" /> auto-approve
        </label>
        <button onClick={() => onAct(() => adminKbSetLearningWindow(row.domain, { windowMonths: months, cadenceDays: cadence, autoApprove: auto }))}
          disabled={busy} className="text-xs text-secondary-500 hover:text-secondary-900 dark:hover:text-white disabled:opacity-40">Save</button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button onClick={onLearn} disabled={busy}
          title={pending ? 'Run another research cycle — it ACCUMULATES onto this pending candidate (evidence compounds), then re-benchmarks. Publish when the numbers look right.' : 'Research this domain and build a pending candidate to review before publishing.'}
          className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-700 px-3 py-1.5 text-sm hover:bg-secondary-100 dark:hover:bg-secondary-800 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : pending ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />} {busy ? (pending ? 'Iterating…' : 'Learning…') : pending ? 'Iterate candidate' : 'Learn now'}
        </button>
        <button onClick={() => onAct(() => adminKbApproveCandidate(row.domain))} disabled={busy || !pending}
          className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 text-white dark:bg-white dark:text-secondary-900 px-3 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-40">
          <Check className="w-4 h-4" /> Publish
        </button>
        <button onClick={() => onAct(() => adminKbRejectCandidate(row.domain))} disabled={busy || !pending}
          className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-700 px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 disabled:opacity-40">
          <X className="w-4 h-4" /> Discard
        </button>
        <button onClick={() => setShowSources((s) => !s)}
          className="ml-auto inline-flex items-center gap-1.5 text-sm text-secondary-500 hover:text-secondary-900 dark:hover:text-white">
          <Globe className="w-4 h-4" /> Sources
        </button>
      </div>

      {showSources && <SourceRegistryEditor domain={row.domain} />}
    </div>
  );
};

/** Admin review board for the self-learning living layer: every domain's pending update, its evidence, and the
 *  publish/discard controls. Nothing goes live without an explicit approve (or an opted-in auto-approve). */
const LearningPanel: React.FC = () => {
  const [rows, setRows] = useState<KbCandidateRow[] | null>(null);
  const [error, setError] = useState('');
  const [busyDomain, setBusyDomain] = useState<string | null>(null);

  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const load = useCallback(async () => {
    setError('');
    try { const r = await adminKbCandidates(); if (aliveRef.current) setRows(r); }
    catch (e: any) { if (aliveRef.current) setError(e?.message || 'Failed to load learning status'); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const actOn = useCallback((domain: string) => (fn: () => Promise<unknown>) => {
    setBusyDomain(domain); setError('');
    void fn().then(() => load()).catch((e: any) => setError(e?.message || 'Action failed')).finally(() => setBusyDomain(null));
  }, [load]);

  // "Learn now" runs ~30s of real research server-side, so it's a background job we POLL — no long-hanging request.
  const learn = useCallback((domain: string) => {
    setBusyDomain(domain); setError('');
    adminKbLearnDomain(domain).then(({ jobId }) => {
      const poll = () => adminKbLearnStatus(jobId).then((job) => {
        if (!aliveRef.current) return;
        if (job.status === 'running') { window.setTimeout(poll, 2500); return; }
        if (job.status === 'error') setError(`Learn failed: ${job.error || 'unknown error'}`);
        void load(); setBusyDomain(null);
      }).catch((e: any) => { if (aliveRef.current) { setError(e?.message || 'Failed to poll learn job'); setBusyDomain(null); } });
      poll();
    }).catch((e: any) => { setError(e?.message || 'Failed to start learn'); setBusyDomain(null); });
  }, [load]);

  const pendingDomains = (rows || []).filter((r) => r.status === 'pending_review').map((r) => r.domain);
  const pending = pendingDomains.length;
  const [approvingAll, setApprovingAll] = useState(false);
  const [confirm, confirmDialog] = useConfirm();

  // Publish EVERY pending update in one go (sequential, reusing the per-domain approve). Confirmed — each goes live.
  const approveAll = useCallback(async () => {
    const domains = (rows || []).filter((r) => r.status === 'pending_review').map((r) => r.domain);
    if (!domains.length || !(await confirm({ title: 'Publish all pending updates?', description: `Publish all ${domains.length} pending update${domains.length === 1 ? '' : 's'}? Each goes live immediately (the prior version is archived, so it's reversible).`, confirmText: 'Publish all' }))) return;
    setApprovingAll(true); setError('');
    let failed = 0;
    for (const d of domains) { try { await adminKbApproveCandidate(d); } catch { failed++; } }
    if (aliveRef.current) { setApprovingAll(false); if (failed) setError(`${failed} of ${domains.length} failed to publish.`); void load(); }
  }, [rows, load, confirm]);

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Agent learning</h2>
          <p className="mt-0.5 text-sm text-secondary-500 max-w-2xl">
            Agents research their fields weekly and stage an update per domain. Review the evidence, then publish or discard — nothing goes live without you.
          </p>
        </div>
        <button onClick={() => void load()} disabled={busyDomain !== null}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-700 px-3 py-1.5 text-sm hover:bg-secondary-100 dark:hover:bg-secondary-800 disabled:opacity-40">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="mt-4"><SelfTestSection domains={(rows || []).map((r) => r.domain)} /></div>

      {pending > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-secondary-900 dark:text-white">{pending} update{pending === 1 ? '' : 's'} awaiting review</span>
          <button onClick={() => void approveAll()} disabled={approvingAll || busyDomain !== null}
            className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 dark:bg-white px-3 py-1.5 text-xs font-semibold text-white dark:text-secondary-900 hover:opacity-90 disabled:opacity-40">
            {approvingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Approve all ({pending})
          </button>
        </div>
      )}
      {error && <div className="mt-3 flex items-center gap-2 rounded-lg bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm text-rose-600"><AlertCircle className="w-4 h-4 shrink-0" />{error}</div>}

      {rows === null ? (
        <div className="mt-10 flex justify-center text-secondary-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="mt-10 text-center text-sm text-secondary-500">No learning domains yet.</div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {rows.map((r) => <CandidateCard key={r.domain} row={r} busy={busyDomain === r.domain} onAct={actOn(r.domain)} onLearn={() => learn(r.domain)} />)}
        </div>
      )}
      {confirmDialog}
    </div>
  );
};

export default LearningPanel;
